/**
 * Client-generated ULIDs (`060-notebook-tree.md` §6, `111-frontend-architecture.md` §4.1).
 *
 * The ids are generated HERE, not by the server. That is the entire reason a new page
 * can appear in the tree with its FINAL id before the network is touched, and it is why
 * there is no temp-id → real-id reconciliation anywhere in this codebase. There must
 * never be one: it is a class of bug (dangling references, lost navigations, orphaned
 * drafts) we bought our way out of with 16 bytes.
 *
 * ~40 lines rather than a dependency, exactly as 060 §6 asks.
 *
 * Monotonic within a millisecond: two pages created in the same tick must not collide,
 * and they must sort in creation order. Same-millisecond calls increment the random
 * component instead of redrawing it (the standard ULID monotonic rule).
 */

/** Crockford base32 — no I, L, O or U. Sorts lexicographically in byte order. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const TIME_LEN = 10;
const RANDOM_LEN = 16;

let lastTime = -1;
let lastRandom: number[] = [];

function randomBytes(length: number): number[] {
  const values = new Uint8Array(length);
  crypto.getRandomValues(values);
  // 0-255 → 0-31. A modulo would bias, but 256 is a multiple of 32, so it does not.
  return Array.from(values, (byte) => byte % 32);
}

function encodeTime(now: number): string {
  let time = now;
  let out = '';
  for (let i = 0; i < TIME_LEN; i++) {
    const mod = time % 32;
    out = (ALPHABET[mod] ?? '0') + out;
    time = (time - mod) / 32;
  }
  return out;
}

/** Increment the random component in place, right to left, carrying on overflow. */
function incrementRandom(random: number[]): number[] {
  const next = [...random];
  for (let i = next.length - 1; i >= 0; i--) {
    const value = next[i] ?? 0;
    if (value < 31) {
      next[i] = value + 1;
      return next;
    }
    next[i] = 0;
  }
  // 32^16 same-millisecond ids. Not reachable; redraw rather than return a wrapped id.
  return randomBytes(RANDOM_LEN);
}

export function ulid(now: number = Date.now()): string {
  if (now === lastTime) {
    lastRandom = incrementRandom(lastRandom);
  } else {
    lastTime = now;
    lastRandom = randomBytes(RANDOM_LEN);
  }

  return encodeTime(now) + lastRandom.map((value) => ALPHABET[value] ?? '0').join('');
}

const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;

export function isUlid(value: string): boolean {
  return ULID_RE.test(value);
}
